import { useState } from 'react'
import { Card, CardHeader, CardTitle, CardDescription, CardContent, Button, Textarea } from '@/components/ui'
import { FileText, Copy, Check, Download, Eye, Code, Bold, Italic, List, Link, Image, Heading } from 'lucide-react'
import { toast } from 'sonner'

const sampleMarkdown = `# Welcome to Markdown Editor

This is a **live preview** markdown editor. You can write markdown on the left and see the preview on the right.

## Features

- **Bold** and *italic* text
- [Links](https://example.com)
- Lists and checkboxes
- Code blocks
- And much more!

### Code Example

\`\`\`javascript
const greeting = "Hello, World!";
console.log(greeting);
\`\`\`

### Table Example

| Name | Age | City |
|------|-----|------|
| John | 25  | NYC  |
| Jane | 30  | LA   |

### Blockquote

> This is a blockquote. It can span multiple lines and is great for highlighting important information.

---

**Happy writing!**
`

export function MarkdownEditor() {
  const [markdown, setMarkdown] = useState(sampleMarkdown)
  const [copied, setCopied] = useState(false)
  const [view, setView] = useState<'split' | 'edit' | 'preview'>('split')

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(markdown)
      setCopied(true)
      toast.success('Markdown copied to clipboard!')
      setTimeout(() => setCopied(false), 2000)
    } catch {
      toast.error('Failed to copy')
    }
  }

  const downloadMarkdown = () => {
    const blob = new Blob([markdown], { type: 'text/markdown' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'document.md'
    link.click()
    URL.revokeObjectURL(url)
    toast.success('Markdown file downloaded!')
  }

  const insertText = (before: string, after: string = '', placeholder: string = '') => {
    const textarea = document.querySelector('textarea') as HTMLTextAreaElement
    if (!textarea) return

    const start = textarea.selectionStart
    const end = textarea.selectionEnd
    const selectedText = markdown.substring(start, end) || placeholder

    const newText = markdown.substring(0, start) + before + selectedText + after + markdown.substring(end)
    setMarkdown(newText)

    // Set cursor position
    setTimeout(() => {
      textarea.focus()
      const newPos = start + before.length + selectedText.length + after.length
      textarea.setSelectionRange(newPos, newPos)
    }, 0)
  }

  const parseMarkdown = (md: string): string => {
    let html = md
      // Escape HTML
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')

      // Code blocks (before other processing)
      .replace(/```(\w*)\n([\s\S]*?)```/g, '<pre class="bg-gray-800 text-gray-100 p-4 rounded-lg overflow-x-auto my-4"><code>$2</code></pre>')

      // Inline code
      .replace(/`([^`]+)`/g, '<code class="bg-gray-200 dark:bg-gray-700 px-1 rounded text-sm">$1</code>')

      // Headers
      .replace(/^### (.*$)/gm, '<h3 class="text-lg font-bold mt-6 mb-2">$1</h3>')
      .replace(/^## (.*$)/gm, '<h2 class="text-xl font-bold mt-6 mb-3">$1</h2>')
      .replace(/^# (.*$)/gm, '<h1 class="text-2xl font-bold mt-6 mb-4">$1</h1>')

      // Bold and Italic
      .replace(/\*\*\*(.*?)\*\*\*/g, '<strong><em>$1</em></strong>')
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')

      // Links and Images
      .replace(/!\[(.*?)\]\((.*?)\)/g, '<img src="$2" alt="$1" class="max-w-full rounded my-4">')
      .replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2" class="text-primary hover:underline">$1</a>')

      // Blockquotes
      .replace(/^&gt; (.*$)/gm, '<blockquote class="border-l-4 border-primary pl-4 py-2 my-4 text-muted-foreground italic">$1</blockquote>')

      // Horizontal rules
      .replace(/^---$/gm, '<hr class="my-6 border-border">')

      // Unordered lists
      .replace(/^\- (.*$)/gm, '<li class="ml-4">$1</li>')
      .replace(/^\* (.*$)/gm, '<li class="ml-4">$1</li>')

      // Tables (simplified)
      .replace(/\|(.+)\|/g, (match) => {
        const cells = match.split('|').filter(c => c.trim())
        if (cells.every(c => c.trim().match(/^-+$/))) {
          return '' // Skip separator row
        }
        const cellHtml = cells.map(c => `<td class="border border-border px-3 py-2">${c.trim()}</td>`).join('')
        return `<tr>${cellHtml}</tr>`
      })

      // Wrap consecutive list items
      .replace(/(<li.*<\/li>\n?)+/g, '<ul class="list-disc my-4">$&</ul>')

      // Wrap table rows
      .replace(/(<tr>.*<\/tr>\n?)+/g, '<table class="w-full border-collapse my-4">$&</table>')

      // Line breaks (paragraphs)
      .replace(/\n\n/g, '</p><p class="my-4">')
      .replace(/\n/g, '<br>')

    return `<p class="my-4">${html}</p>`
  }

  const toolbarButtons = [
    { icon: Bold, action: () => insertText('**', '**', 'bold text'), title: 'Bold' },
    { icon: Italic, action: () => insertText('*', '*', 'italic text'), title: 'Italic' },
    { icon: Heading, action: () => insertText('## ', '', 'Heading'), title: 'Heading' },
    { icon: List, action: () => insertText('- ', '', 'List item'), title: 'List' },
    { icon: Link, action: () => insertText('[', '](url)', 'link text'), title: 'Link' },
    { icon: Image, action: () => insertText('![alt](', ')', 'image-url'), title: 'Image' },
    { icon: Code, action: () => insertText('```\n', '\n```', 'code'), title: 'Code Block' },
  ]

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
            <FileText className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Markdown Editor</h1>
            <p className="text-muted-foreground">Write and preview markdown in real-time</p>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <Card className="glass">
        <CardContent className="p-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-wrap gap-1">
              {toolbarButtons.map(({ icon: Icon, action, title }) => (
                <Button
                  key={title}
                  variant="ghost"
                  size="sm"
                  onClick={action}
                  title={title}
                  className="h-8 w-8 p-0"
                >
                  <Icon className="h-4 w-4" />
                </Button>
              ))}
            </div>

            <div className="flex items-center gap-2">
              <div className="flex bg-muted rounded-lg p-1">
                <Button
                  variant={view === 'edit' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setView('edit')}
                  className="h-7"
                >
                  <Code className="h-4 w-4 mr-1" />
                  Edit
                </Button>
                <Button
                  variant={view === 'split' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setView('split')}
                  className="h-7"
                >
                  Split
                </Button>
                <Button
                  variant={view === 'preview' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setView('preview')}
                  className="h-7"
                >
                  <Eye className="h-4 w-4 mr-1" />
                  Preview
                </Button>
              </div>

              <Button variant="outline" size="sm" onClick={copyToClipboard}>
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </Button>
              <Button variant="outline" size="sm" onClick={downloadMarkdown}>
                <Download className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Editor */}
      <div className={`grid gap-4 ${view === 'split' ? 'lg:grid-cols-2' : ''}`}>
        {(view === 'edit' || view === 'split') && (
          <Card className="glass">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Code className="h-4 w-4" />
                Markdown
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                value={markdown}
                onChange={(e) => setMarkdown(e.target.value)}
                className="min-h-[500px] font-mono text-sm resize-none"
                placeholder="Write your markdown here..."
              />
            </CardContent>
          </Card>
        )}

        {(view === 'preview' || view === 'split') && (
          <Card className="glass">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Eye className="h-4 w-4" />
                Preview
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div
                className="min-h-[500px] prose prose-sm dark:prose-invert max-w-none overflow-auto"
                dangerouslySetInnerHTML={{ __html: parseMarkdown(markdown) }}
              />
            </CardContent>
          </Card>
        )}
      </div>

      {/* Stats */}
      <Card className="glass">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-6 text-sm">
            <div>
              <span className="text-muted-foreground">Characters:</span>{' '}
              <span className="font-mono">{markdown.length}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Words:</span>{' '}
              <span className="font-mono">{markdown.trim() ? markdown.trim().split(/\s+/).length : 0}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Lines:</span>{' '}
              <span className="font-mono">{markdown.split('\n').length}</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
